import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
  Input,
} from "reactstrap";
import { updateCuttingGroup } from "./service";
import { Notification } from "../common/style";

const CuttingPlanEditModal = ({ orderId, groups }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [groupData, setGroupData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isNotificationVisible, setIsNotificationVisible] = useState(true);
  const [notification, setNotification] = useState({ type: "", message: "" });

  useEffect(() => {
    if (isModalOpen) {
      const initialGroupData = groups.map((group) => {
        const boardCount = group.boards.length;
        const currentMaterial = group.board_options?.find(
          (opt) => opt.name === group.material_name || opt.identifier === group.core_material
        );
        const selectedMaterialIdentifier = currentMaterial?.identifier || 
          (group.board_options && group.board_options.length > 0 ? group.board_options[0].identifier : "");
        
        return {
          identifier: group.identifier,
          material_name: group.material_name,
          core_material: selectedMaterialIdentifier,
          board_options: group.board_options || [],
          side_1_color: group.side_1_color,
          side_2_color: group.side_2_color,
          board_count: boardCount,
          new_board_quantity: 
            group.new_board_quantity !== undefined && group.new_board_quantity !== null && group.new_board_quantity !== ""
              ? group.new_board_quantity 
              : (boardCount > 0 ? boardCount.toString() : ""),
          new_max_wastage: 
            group.new_max_wastage !== undefined && group.new_max_wastage !== null && group.new_max_wastage !== ""
              ? group.new_max_wastage 
              : "0",
        };
      });
      setGroupData(initialGroupData);
    }
  }, [isModalOpen, groups]);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const onDismissNotification = () => setIsNotificationVisible(false);

  const handleInputChange = (index, field, value) => {
    const newGroupData = [...groupData];
    newGroupData[index][field] = value;
    
    if (field === "core_material") {
      const selectedOption = newGroupData[index].board_options?.find(
        (opt) => opt.identifier === value
      );
      if (selectedOption) {
        newGroupData[index].material_name = selectedOption.name;
      }
    }
    
    setGroupData(newGroupData);
  };

  const validateInput = (value) => {
    // Only allow numbers, decimal point, and empty string
    const regex = /^[0-9]*\.?[0-9]*$/;
    return regex.test(value);
  };

const isValidForm = () => {
    return groupData.every((group) => {
      const boardQuantity = parseFloat(group.new_board_quantity);
      const maxWastage = parseFloat(group.new_max_wastage);

      return (
        group.new_board_quantity !== "" &&
        group.new_max_wastage !== "" &&
        boardQuantity > 0 &&
        maxWastage >= 0 &&
        validateInput(group.new_board_quantity) &&
        validateInput(group.new_max_wastage)
      );
    });
  };

  const handleSubmit = () => {
    const payload = groupData.map((data) => {
      const payloadItem = {
        identifier: data.identifier,
        core_material: data.core_material,
      };
      
      if (data.new_board_quantity !== "" && data.new_board_quantity !== undefined) {
        payloadItem.new_board_quantity = parseFloat(data.new_board_quantity);
      }
      if (data.new_max_wastage !== "" && data.new_max_wastage !== undefined) {
        payloadItem.new_max_wastage = parseFloat(data.new_max_wastage);
      }
      
      return payloadItem;
    });

    setIsLoading(true);

    updateCuttingGroup({
      orderId,
      payload: payload,
    })
      .then(() => {
        toggleModal();
        setIsNotificationVisible(true);
        setNotification({ message: "Cutting Plan Updated", type: "success" });
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotification({ message: error, type: "error" });
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      {notification.type && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notification.message}
        </Notification>
      )}
      <Button className="rounded-1" color="primary" onClick={toggleModal}>
        Edit Cutting Group
      </Button>
      <Modal isOpen={isModalOpen} size="lg" toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Edit Cutting Group</ModalHeader>
        <ModalBody>
          <Table responsive striped>
            <thead>
              <tr>
                <th>Group</th>
                <th>Quantity</th>
                <th>Max Wastage</th>
              </tr>
            </thead>
            <tbody>
              {groupData.map((group, index) => (
                <tr key={group.identifier}>
                  <td>
                    <div>
                      <div className="fw-bold d-flex justify-content-between">
                        <Input
                          type="select"
                          className="form-control-sm d-inline-block p-1 mb-1"
                          style={{ width: "auto", maxWidth: "180px" }}
                          value={group.core_material || ""}
                          onChange={(e) => {
                            handleInputChange(index, "core_material", e.target.value);
                          }}
                        >
                          {group.board_options && group.board_options.length > 0 ? (
                            group.board_options.map((option) => (
                              <option key={option.identifier} value={option.identifier}>
                                {option.name}
                              </option>
                            ))
                          ) : (
                            <option value="">No options available</option>
                          )}
                        </Input>
                        <span className="text-muted">
                          x {group.board_count}
                        </span>
                      </div>
                      <div className="text-muted small">
                        {group.side_1_color}
                      </div>
                      <div className="text-muted small">
                        {group.side_2_color}
                      </div>
                    </div>
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={group.new_board_quantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || validateInput(value)) {
                          handleInputChange(index, "new_board_quantity", value);
                        }
                      }}
                      placeholder="Enter quantity"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={group.new_max_wastage}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || validateInput(value)) {
                          handleInputChange(index, "new_max_wastage", value);
                        }
                      }}
                      placeholder="Enter wastage"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </ModalBody>
        <ModalFooter>
          <Button onClick={toggleModal}>Cancel</Button>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading || !isValidForm()}
          >
            {isLoading ? (
              <>
                <Spinner size="sm" /> &nbsp;
                <span>Loading...</span>
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default CuttingPlanEditModal;
